import {
  type MultiAccountAccountData,
  type MultiAccountEndCustomerData,
  useMultiAccountSearchData,
} from "./MultiAccountSearchDataProvider";
import { type ChangeEvent, type MouseEvent, type ReactNode, useCallback, useState } from "react";
import { MenuItem } from "../../../../../common/components/Menu/MenuItem";
import { Select } from "../../../../../common/components/Select/Select";
import { ToggleIcon } from "../../../../../common/components/icon/ToggleIcon";
import Checkbox from "../../../../../common/components/Checkbox";

import classes from "./MultiAccountSelect.module.less";
import classNames from "classnames";
import { useBoolMap } from "../../../../../common/hooks/useBoolMap";
import { groupByFn, toMapFn } from "../../../../../utils/reducers";
import { isMultiAccountOrderSearchEnabled } from "../../../../../utils/features";
import { useTranslation, type WithTranslation } from "react-i18next";

interface Value {
  accountCode: string;
  endCustomerCode: string;
  salesOrg: string;
}

type Item<T> = NodeItem<T> | ValueItem<T>;

interface NodeItem<T> {
  key: string;
  level: number; // helper
  label: string;
  subLabel: string;
  children: Item<T>[];
}

interface ValueItem<T> {
  key: string;
  level: number; // helper
  label: string;
  subLabel: string;
  value: T;
}

function isNodeItem<T>(node: Item<T>): node is NodeItem<T> {
  return "children" in node;
}
function isValueItem<T>(node: Item<T>): node is ValueItem<T> {
  return !isNodeItem(node);
}

function gatherToggleableKeys(item: Item<unknown>): string[] {
  if (isNodeItem(item)) {
    return item.children.flatMap(gatherToggleableKeys);
  }
  return [item.key];
}

function findNode<T>(nodes: Item<T>[], key: string): Item<T> | undefined {
  const nodesToCheck = [...nodes];

  while (nodesToCheck.length > 0) {
    const node = nodesToCheck.shift()!;
    if (node.key === key) {
      return node;
    }
    if (isNodeItem(node)) {
      nodesToCheck.push(...node.children);
    }
  }
  return undefined;
}

function isCheckboxSelected(
  selectedValues: string[],
  node: Item<unknown>,
): {
  checked: boolean;
  indeterminate: boolean;
} {
  if (isNodeItem(node)) {
    return isAllSelected(selectedValues, node.children);
  } else {
    return {
      checked: selectedValues.includes(node.key),
      indeterminate: false,
    };
  }
}

function isAllSelected(
  selectedValues: string[],
  nodes: Item<unknown>[],
): {
  checked: boolean;
  indeterminate: boolean;
} {
  if (!nodes.length) {
    return {
      checked: false,
      indeterminate: false,
    };
  }
  let atLeastOneSelected = false;
  let atLeastOneDeselected = false;
  for (const node of nodes) {
    const nodeStatus = isCheckboxSelected(selectedValues, node);
    if (nodeStatus.indeterminate) {
      return nodeStatus;
    } else if (nodeStatus.checked) {
      atLeastOneSelected = true;
    } else {
      atLeastOneDeselected = true;
    }
  }
  return {
    checked: atLeastOneSelected,
    indeterminate: atLeastOneSelected && atLeastOneDeselected,
  };
}

function flatten<T>(
  items: Item<T>[],
  expandedItems: Dict<boolean>,
  parentIsVisible = true,
): { item: Item<T>; isVisible: boolean }[] {
  return items.flatMap((item) => {
    if (isNodeItem(item)) {
      return [
        {
          item: item,
          isVisible: parentIsVisible,
        },
        ...flatten(item.children, expandedItems, parentIsVisible && expandedItems[item.key]),
      ];
    }
    return [
      {
        item: item,
        isVisible: parentIsVisible,
      },
    ];
  });
}

interface Props {
  onChange?: (newValues: string[]) => void;
  className?: string;
  values: string[];
}

function transformEndCustomer(
  acc: MultiAccountAccountData,
  endCustomer: MultiAccountEndCustomerData,
): ValueItem<Value> {
  return {
    key: acc.code + "-" + endCustomer.code + "-" + endCustomer.salesOrg,
    label: endCustomer.name,
    level: 1,
    subLabel: endCustomer.code,
    value: {
      accountCode: acc.code,
      endCustomerCode: endCustomer.code,
      salesOrg: endCustomer.salesOrg,
    },
  };
}

function transformAccount(account: MultiAccountAccountData): NodeItem<Value> {
  const children = account.endCustomer.map((endCustomer) => transformEndCustomer(account, endCustomer));
  return {
    key: account.code,
    level: 0,
    label: account.name,
    subLabel: account.shortCode,
    children: children,
  };
}

export const MultiAccountSelect = ({ values, onChange }: Props) => {
  const multiAccountData = useMultiAccountSearchData();

  const { t } = useTranslation();

  const [items] = useState<Item<Value>[]>(() => multiAccountData.accounts.flatMap(transformAccount));

  const { map: expandedItems, toggle: toggleExpanded } = useBoolMap(() =>
    toMapFn(
      items.filter(isNodeItem),
      (item) => item.key,
      () => false,
    ),
  );

  const onExpand = useCallback(
    (item: Item<unknown>) => {
      toggleExpanded(item.key);
    },
    [toggleExpanded],
  );

  const onToggle = useCallback(
    (item: Item<unknown> | Item<unknown>[]) => {
      if (!onChange) {
        return;
      }

      if (!Array.isArray(item)) {
        item = [item];
      }

      if (!item.length) {
        return;
      }

      const newValue = !item.every((x) => {
        const state = isCheckboxSelected(values, x);
        return state.checked && !state.indeterminate;
      });
      const keysToChange = item.flatMap((x) => gatherToggleableKeys(x));

      const map = toMapFn(values, (i) => i);
      if (newValue) {
        keysToChange.forEach((key) => (map[key] = key));
      } else {
        keysToChange.forEach((key) => delete map[key]);
      }
      onChange(Object.keys(map));
    },
    [onChange, values],
  );

  const elements = createItems(items, expandedItems, onExpand, values, onToggle, t);

  return (
    <div className={"scha__selection__multiple-levels"}>
      <Select
        variant={"outlined"}
        label={t("orderListPage.search.multiaccount")}
        renderValue={(selectedValues: unknown) => {
          const parts = (selectedValues as string[]).map((val) => val.split("-"));

          const accounts = Object.entries(
            groupByFn(
              parts,
              (item) => item[0],
              () => true,
            ),
          ).map(([account, countingList]) => ({
            name: multiAccountData.accounts.find((_) => _.code === account)!.name,
            count: countingList.length,
          }));

          let maxLen = 13;
          if (accounts.length === 1) {
            maxLen = 99;
          } else if (accounts.length === 2) {
            maxLen = 20;
          }

          return accounts
            .map(({ name, count }) => {
              name = name.length > maxLen ? name.substring(0, maxLen - 3) + "..." : name;
              return `${name} (${count})`;
            })
            .join(", ");
        }}
        multiple={true}
        value={values}
        // onBlur={onBlur}
        classes={{ root: "scha__selection__multiple-levels__main" }}
        MenuProps={{
          classes: {
            list: "scha__selection__multiple-levels__popup__list",
            paper: "scha__selection__multiple-levels__popup__holder",
          },
        }}
        // error={!!error}
        // helperLabel={error}
        fullWidth={true}
        // disabled={disabled}
      >
        {elements}
      </Select>
    </div>
  );
};

const createItems = (
  list: Item<Value>[],
  expandedItems: Dict<boolean>,
  onExpand: (item: Item<Value>) => void,
  selectedItems: string[],
  onSelect: (item: Item<Value> | Item<Value>[]) => void,
  t: WithTranslation["t"],
): ReactNode[] => {
  const renderValueItem = (data: ValueItem<Value>, isVisible: boolean): ReactNode => {
    if (!isVisible) {
      return null;
    }
    const level = data.level;
    return (
      <MenuItem key={data.key} className={classNames("option level-" + level, classes.nodeItem)} value={data.key}>
        <div style={calcStyles(level)} />
        <div>
          <div className={classes.itemUpperRow}>
            <Checkbox
              {...isCheckboxSelected(selectedItems, data)}
              onClick={(e) => {
                onSelect(data);
                NOOP(e);
              }}
            />
            {data.label}
          </div>
          <div className={classes.itemLowerRow}>{data.subLabel}</div>
        </div>
      </MenuItem>
    );
  };

  const renderNodeItem = (data: NodeItem<Value>, isVisible: boolean): ReactNode => {
    const level = data.level;
    return (
      <MenuItem
        key={data.key}
        className={classNames("option title level-" + level, classes.accountItem)}
        value={data.key}
        hidden={!isVisible}
      >
        <div style={calcStyles(level)} />
        <ToggleIcon
          className={classes.accountArrow}
          isActive={expandedItems[data.key]}
          onClick={(e) => {
            onExpand(data);
            NOOP(e);
          }}
        />
        <div>
          <div className={classes.itemUpperRow}>
            <Checkbox
              {...isCheckboxSelected(selectedItems, data)}
              onClick={(e) => {
                onSelect(data);
                NOOP(e);
              }}
            />
            {data.label}
          </div>
          <div className={classes.itemLowerRow}>{data.subLabel}</div>
        </div>
      </MenuItem>
    );
  };

  const items = flatten(list, expandedItems).map((data) =>
    isNodeItem(data.item) ? renderNodeItem(data.item, data.isVisible) : renderValueItem(data.item, data.isVisible),
  );

  items.unshift(
    <MenuItem key={"select-all"} className={classNames("option title level-0", classes.allItem)} value={"select-all"}>
      <div style={calcStyles(0)} />
      <div>
        <div className={classes.itemUpperRow}>
          <Checkbox
            {...isAllSelected(selectedItems, list)}
            onClick={(e) => {
              onSelect(list);
              NOOP(e);
            }}
          />
          {t("common.all")}
        </div>
      </div>
    </MenuItem>,
  );

  return items;
};

const NOOP = (e: MouseEvent<unknown> | ChangeEvent<unknown>) => {
  e.preventDefault();
  e.stopPropagation();
};

const indentLeftSize: number = 15; // TODO change to 16 with checkbox change
const calcStyles = (level: number) => {
  return { paddingLeft: level * indentLeftSize + "px", marginLeft: "-8px" };
};

export function getInitialAccounts() {
  if (!isMultiAccountOrderSearchEnabled()) {
    return [];
  }

  const { uid, endCustomerCode, salesOrg } = window.SCHA.user.selectedAccount;

  return [[uid, endCustomerCode, salesOrg].join("-")];
}
